import type { PricingResponse, PricingItem } from '../../api/models/bff/Price';
import type { AncillaryBreakdownItem } from '../../api/models/bff/Ancillaries';
import type { QuotationPricingItemType } from '../../api/models/bff/Quotation';

/**
 * Create mock pricing item
 *
 * @param type - usage type
 * @param amount - amount
 * @param currency - currency
 * @returns Mock pricing item
 */
export function createMockPricingItem(type: QuotationPricingItemType, amount: string, currency: string = 'EUR'): PricingItem {
  return {
    type,
    price: {
      amount,
      currency,
    },
  };
}

/**
 * Create mock ancillary item
 *
 * @param type - ancillary type (with or without ANCILLARY: prefix)
 * @param amount - price amount
 * @param quantity - item quantity
 * @param included - whether the item is included
 * @param currency - currency
 * @returns Mock ancillary item
 */
export function createMockAncillaryItem(
  type: string,
  amount: string,
  quantity: number = 1,
  currency: string = 'EUR',
): AncillaryBreakdownItem {
  return {
    type,
    quantity,
    price: { amount, currency },
  };
}

/**
 * Create mock pricing response
 *
 * @param overrides - Partial overrides for the response
 * @returns Mock pricing response
 */
export interface MockCredits {
  before: number;
  used: number;
  remaining: number;
}

/**
 * Create mock pricing response with credits breakdown
 *
 * @param credits - Optional credits override
 * @param overrides - Partial overrides for the response
 * @returns Mock pricing response including credits
 */
export function createMockPricingWithCredits(overrides?: Partial<PricingResponse>): PricingResponse {
  const base = createMockPricing(overrides);
  return {
    ...base,
    summary: {
      ...base.summary,
      credits: {},
    },
  } as PricingResponse;
}

export function createMockPricing(overrides?: Partial<PricingResponse>): PricingResponse {
  return {
    passengerPrices: [],
    summary: {
      total: { amount: '178.00', currency: 'EUR' },
      breakdown: {
        baseFare: {
          total: { amount: '50.00', currency: 'EUR' },
          items: [
            createMockPricingItem('BASE_FARE', '50.00', 'EUR'),
          ],
        },
        taxes: {
          total: { amount: '100.00', currency: 'EUR' },
          items: [
            createMockPricingItem('VAT', '21.00', 'EUR'),
            createMockPricingItem('ENVIRONMENTAL', '79.00', 'EUR'),
          ],
        },
        fees: {
          total: { amount: '28.00', currency: 'EUR' },
          items: [
            createMockPricingItem('AIRPORT', '20.00', 'EUR'),
            createMockPricingItem('OTHER', '8.00', 'EUR'),
          ],
        },
        ancillaries: {
          total: { amount: '150.00', currency: 'EUR' },
          items: [
            {
              type: 'BG30',
              quantity: 2,
              price: { amount: '0.00', currency: 'EUR' },
            },
          ],
        },
      },
    },
    ...overrides,
  };
}
